import {
  Type,
  ListChecks,
  ChevronsRight,
  Code2,
  Columns3,
  Image as ImageIcon,
  Activity,
  ListOrdered,
  BookOpen,
  Lightbulb,
  LayoutGrid,
  Minus,
  Info,
  Sigma,
  ArrowLeftRight,
  ListTodo,
  Camera,
  Target,
  Flame,
  Flag,
  GraduationCap,
  BarChart3,
  CalendarRange,
  CalendarDays,
  Quote,
  ScrollText,
  UserRound,
  CheckSquare,
} from 'lucide-react'

import {
  BLOCK_LAUNCHER_ENTRIES,
  BLOCKS_REGISTRY,
  getBlockDefaultData,
  isBlockType,
  blockRegistryEntry,
  resolveBlockType,
} from './blocks-registry'

/** @typedef {'blocks'|'writing'|'organization'|'visual'|'advanced'|'tracking'} LauncherCategoryId */

export const CATEGORIES = [
  { id: 'blocks', label: 'Blocks' },
  { id: 'writing', label: 'Writing' },
  { id: 'organization', label: 'Structure' },
  { id: 'visual', label: 'Visual' },
  { id: 'tracking', label: 'Tracking' },
  { id: 'advanced', label: 'Advanced' },
]

/** Spec-aligned category metadata */
export const SECTION_CATEGORIES = {
  content: { label: 'Writing', color: 'blue', launcherId: 'writing' },
  thinking: { label: 'Thinking', color: 'purple', launcherId: 'organization' },
  tracking: { label: 'Tracking', color: 'green', launcherId: 'tracking' },
  structure: { label: 'Structure', color: 'amber', launcherId: 'organization' },
  visual: { label: 'Visual', color: 'red', launcherId: 'visual' },
  advanced: { label: 'Advanced', color: 'amber', launcherId: 'advanced' },
}

const DEFAULT_DATA = {
  callout: { variant: 'tip', title: '', body: '' },
  code: { language: 'javascript', code: '// start typing...' },
  compare: {
    columns: [
      { label: 'Option A', color: 'green', items: [] },
      { label: 'Option B', color: 'amber', items: [] },
      { label: 'Option C', color: 'purple', items: [] },
    ],
  },
  content: { points: [] },
  decision: {
    context: '',
    options: [],
    decision: '',
    reasoning: '',
    decided: false,
    decidedAt: null,
  },
  divider: {},
  equation: { expression: '', caption: '' },
  focus: { label: 'North Star', text: '', subtext: '' },
  insight: { text: '', attribution: '' },
  layout: { columns: 2, left: [], right: [], third: [] },
  list: { variant: 'bullet', items: [] },
  media: { url: '', caption: '', type: 'image' },
  richtext: { content: '' },
  snapshot: { title: '', date: '', pairs: [] },
  status: { metrics: [], viewMode: 'progress' },
  steps: { steps: [] },
  tabs: { tabs: [{ label: 'Tab 1', content: '' }], activeTab: 0 },
  toggle: { title: '', body: '', open: false },
  habit_tracker: { habits: [], streakDays: 0 },
  goal: { title: '', target: 100, current: 0, deadline: '' },
  lesson_tracker: { lessons: [] },
  metric: { label: '', value: 0, trend: 'flat', unit: '' },
  timeline: { events: [] },
  weekly_view: { days: [] },
  mantra: { text: '' },
  rule_list: { rules: [] },
  profile: { name: '', tagline: '', stats: [] },
  checklist: { items: [] },
}

export const SECTIONS_REGISTRY = {
  callout: {
    type: 'callout',
    label: 'Callout',
    description: 'Info, warnings, tips',
    icon: '💬',
    category: 'content',
    defaultData: DEFAULT_DATA.callout,
    implemented: true,
    lucideIcon: Info,
    gradient: 'tile-gradient-1',
    launcherCategory: 'writing',
  },
  code: {
    type: 'code',
    label: 'Code',
    description: 'Snippets with syntax highlighting',
    icon: '</>',
    category: 'content',
    defaultData: DEFAULT_DATA.code,
    implemented: true,
    lucideIcon: Code2,
    gradient: 'tile-gradient-4',
    launcherCategory: 'writing',
  },
  compare: {
    type: 'compare',
    label: 'Compare',
    description: 'Side-by-side analysis',
    icon: '⇄',
    category: 'thinking',
    defaultData: DEFAULT_DATA.compare,
    implemented: true,
    lucideIcon: ArrowLeftRight,
    gradient: 'tile-gradient-7',
    launcherCategory: 'advanced',
  },
  content: {
    type: 'content',
    label: 'Key Points',
    description: 'Summaries and takeaways',
    icon: '★',
    category: 'content',
    defaultData: DEFAULT_DATA.content,
    implemented: true,
    lucideIcon: BookOpen,
    gradient: 'tile-gradient-3',
    launcherCategory: 'writing',
  },
  decision: {
    type: 'decision',
    label: 'Decision',
    description: 'Track options and outcomes',
    icon: '⚖',
    category: 'thinking',
    defaultData: DEFAULT_DATA.decision,
    implemented: true,
    lucideIcon: ListTodo,
    gradient: 'tile-gradient-4',
    launcherCategory: 'organization',
  },
  divider: {
    type: 'divider',
    label: 'Divider',
    description: 'Visual separator',
    icon: '—',
    category: 'structure',
    defaultData: DEFAULT_DATA.divider,
    implemented: true,
    lucideIcon: Minus,
    gradient: 'tile-gradient-5',
    launcherCategory: 'organization',
  },
  equation: {
    type: 'equation',
    label: 'Equation',
    description: 'Math and science formulas',
    icon: 'Σ',
    category: 'advanced',
    defaultData: DEFAULT_DATA.equation,
    implemented: true,
    lucideIcon: Sigma,
    gradient: 'tile-gradient-8',
    launcherCategory: 'advanced',
  },
  focus: {
    type: 'focus',
    label: 'Focus',
    description: 'Critical high-impact point',
    icon: '◎',
    category: 'content',
    defaultData: DEFAULT_DATA.focus,
    implemented: true,
    lucideIcon: Target,
    gradient: 'tile-gradient-2',
    launcherCategory: 'advanced',
  },
  insight: {
    type: 'insight',
    label: 'Insight',
    description: 'Highlight a thought',
    icon: '💡',
    category: 'content',
    defaultData: DEFAULT_DATA.insight,
    implemented: true,
    lucideIcon: Lightbulb,
    gradient: 'tile-gradient-2',
    launcherCategory: 'writing',
  },
  layout: {
    type: 'layout',
    label: 'Columns',
    description: '2 or 3 column layout',
    icon: '⊞',
    category: 'structure',
    defaultData: DEFAULT_DATA.layout,
    implemented: true,
    lucideIcon: Columns3,
    gradient: 'tile-gradient-5',
    launcherCategory: 'organization',
  },
  list: {
    type: 'list',
    label: 'List',
    description: 'Bullets, numbers, checklists',
    icon: '≡',
    category: 'content',
    defaultData: DEFAULT_DATA.list,
    implemented: true,
    lucideIcon: ListChecks,
    gradient: 'tile-gradient-2',
    launcherCategory: 'writing',
  },
  media: {
    type: 'media',
    label: 'Media',
    description: 'Images, video, embeds',
    icon: '▶',
    category: 'visual',
    defaultData: DEFAULT_DATA.media,
    implemented: true,
    lucideIcon: ImageIcon,
    gradient: 'tile-gradient-6',
    launcherCategory: 'visual',
  },
  richtext: {
    type: 'richtext',
    label: 'Rich Text',
    description: 'Paragraphs, headings, quotes',
    icon: 'T',
    category: 'content',
    defaultData: DEFAULT_DATA.richtext,
    implemented: true,
    lucideIcon: Type,
    gradient: 'tile-gradient-1',
    launcherCategory: 'writing',
  },
  snapshot: {
    type: 'snapshot',
    label: 'Snapshot',
    description: 'Featured visual moment',
    icon: '📷',
    category: 'visual',
    defaultData: DEFAULT_DATA.snapshot,
    implemented: true,
    lucideIcon: Camera,
    gradient: 'tile-gradient-6',
    launcherCategory: 'visual',
  },
  status: {
    type: 'status',
    label: 'Status',
    description: 'Progress, metrics, charts',
    icon: '◉',
    category: 'tracking',
    defaultData: DEFAULT_DATA.status,
    implemented: true,
    lucideIcon: Activity,
    gradient: 'tile-gradient-7',
    launcherCategory: 'visual',
  },
  steps: {
    type: 'steps',
    label: 'Steps',
    description: 'Reference workflows',
    icon: '→',
    category: 'content',
    defaultData: DEFAULT_DATA.steps,
    implemented: true,
    lucideIcon: ListOrdered,
    gradient: 'tile-gradient-8',
    launcherCategory: 'advanced',
  },
  tabs: {
    type: 'tabs',
    label: 'Tabs',
    description: 'Grouped content views',
    icon: '⊟',
    category: 'structure',
    defaultData: DEFAULT_DATA.tabs,
    implemented: true,
    lucideIcon: LayoutGrid,
    gradient: 'tile-gradient-4',
    launcherCategory: 'organization',
  },
  toggle: {
    type: 'toggle',
    label: 'Toggle',
    description: 'Collapsible content',
    icon: '▸',
    category: 'content',
    defaultData: DEFAULT_DATA.toggle,
    implemented: true,
    lucideIcon: ChevronsRight,
    gradient: 'tile-gradient-3',
    launcherCategory: 'organization',
  },
  habit_tracker: {
    type: 'habit_tracker',
    label: 'Habit Tracker',
    description: 'Daily/weekly habit with streak counter',
    icon: '🔥',
    category: 'tracking',
    defaultData: DEFAULT_DATA.habit_tracker,
    implemented: false,
    lucideIcon: Flame,
    gradient: 'tile-gradient-7',
    launcherCategory: 'tracking',
  },
  goal: {
    type: 'goal',
    label: 'Goal',
    description: 'Target with progress bar and deadline',
    icon: '🎯',
    category: 'tracking',
    defaultData: DEFAULT_DATA.goal,
    implemented: false,
    lucideIcon: Flag,
    gradient: 'tile-gradient-2',
    launcherCategory: 'tracking',
  },
  lesson_tracker: {
    type: 'lesson_tracker',
    label: 'Lesson Tracker',
    description: 'Cycle items through todo/doing/done',
    icon: '📚',
    category: 'tracking',
    defaultData: DEFAULT_DATA.lesson_tracker,
    implemented: false,
    lucideIcon: GraduationCap,
    gradient: 'tile-gradient-8',
    launcherCategory: 'tracking',
  },
  metric: {
    type: 'metric',
    label: 'Metric',
    description: 'Single big number with label and trend',
    icon: '📊',
    category: 'tracking',
    defaultData: DEFAULT_DATA.metric,
    implemented: false,
    lucideIcon: BarChart3,
    gradient: 'tile-gradient-7',
    launcherCategory: 'tracking',
  },
  timeline: {
    type: 'timeline',
    label: 'Timeline',
    description: 'Ordered events with dates',
    icon: '📅',
    category: 'structure',
    defaultData: DEFAULT_DATA.timeline,
    implemented: false,
    lucideIcon: CalendarRange,
    gradient: 'tile-gradient-5',
    launcherCategory: 'organization',
  },
  weekly_view: {
    type: 'weekly_view',
    label: 'Weekly View',
    description: 'Seven-column day planner',
    icon: '🗓',
    category: 'structure',
    defaultData: DEFAULT_DATA.weekly_view,
    implemented: false,
    lucideIcon: CalendarDays,
    gradient: 'tile-gradient-4',
    launcherCategory: 'organization',
  },
  mantra: {
    type: 'mantra',
    label: 'Mantra',
    description: 'A statement you want to feel, not just read',
    icon: '✦',
    category: 'content',
    defaultData: DEFAULT_DATA.mantra,
    implemented: false,
    lucideIcon: Quote,
    gradient: 'tile-gradient-1',
    launcherCategory: 'writing',
  },
  rule_list: {
    type: 'rule_list',
    label: 'Rule List',
    description: 'Left-accent bordered rules list',
    icon: '§',
    category: 'content',
    defaultData: DEFAULT_DATA.rule_list,
    implemented: false,
    lucideIcon: ScrollText,
    gradient: 'tile-gradient-3',
    launcherCategory: 'writing',
  },
  profile: {
    type: 'profile',
    label: 'Profile',
    description: 'Name, tagline, key stats',
    icon: '👤',
    category: 'content',
    defaultData: DEFAULT_DATA.profile,
    implemented: false,
    lucideIcon: UserRound,
    gradient: 'tile-gradient-6',
    launcherCategory: 'writing',
  },
  checklist: {
    type: 'checklist',
    label: 'Checklist',
    description: 'Simple todo with progress count',
    icon: '☑',
    category: 'content',
    defaultData: DEFAULT_DATA.checklist,
    implemented: false,
    lucideIcon: CheckSquare,
    gradient: 'tile-gradient-2',
    launcherCategory: 'writing',
  },
}

/** Implemented blocks — launcher tiles with live section components */
export const SECTION_DEFS = Object.values(SECTIONS_REGISTRY)
  .filter((e) => e.implemented)
  .map((e) => ({
    type: e.type,
    label: e.label,
    desc: e.description,
    category: e.launcherCategory,
    icon: e.lucideIcon,
    gradient: e.gradient,
  }))

const LEGACY_LAUNCHER_ENTRIES = Object.values(SECTIONS_REGISTRY).map((e) => ({
  type: e.type,
  label: e.label,
  desc: e.description,
  category: e.launcherCategory,
  icon: e.lucideIcon,
  gradient: e.gradient,
  comingSoon: !e.implemented,
  source: 'legacy',
}))

/** Primary launcher: bible-style blocks from lib/notebook/Blocks */
export const LAUNCHER_ENTRIES = BLOCK_LAUNCHER_ENTRIES

export function sectionDefByType(type) {
  const block = blockRegistryEntry(type)
  if (block) {
    return {
      type: block.type,
      label: block.label,
      desc: block.description,
    }
  }
  return SECTION_DEFS.find((s) => s.type === type)
}

export function registryEntry(type) {
  return blockRegistryEntry(type) ?? SECTIONS_REGISTRY[type] ?? null
}

export function getDefaultData(type) {
  return getBlockDefaultData(resolveBlockType(type))
}

export function isImplementedType(type) {
  return isBlockType(type)
}

export { isBlockType, blockRegistryEntry, BLOCKS_REGISTRY }

export function isComingSoonType(type) {
  const entry = SECTIONS_REGISTRY[type]
  return Boolean(entry && !entry.implemented)
}
